import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAssessmentsByEvent } from '../../../firebase';
import MyHeader from '../../../components/common/admin/MyHeader';
import RatingModificationHistory from '../../../components/common/RatingModificationHistory';
import ArtifactsPreview from '../../../components/common/ArtifactsPreview';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { usePrivacy } from '../../../contexts/PrivacyContext';

const WorkAssessmentResults = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { session, groupMembers = [], groupIndex, groups } = location.state || {};
    const { maskName, maskInitial } = usePrivacy();
    const [assessments, setAssessments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const fetchAssessments = async () => {
            if (!session?.eventId) {
                setIsLoading(false);
                return;
            }
            try {
                const data = await getAssessmentsByEvent(session.eventId);
                setAssessments(data);
            } catch (error) {
                console.error('Error fetching assessments:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAssessments();
    }, [session?.eventId]);

    if (!session) {
        return (
            <>
                <MyHeader groupIndex={groupIndex} groups={groups} />
                <PageWrapper>
                    <BackArrowButton onClick={() => navigate('/admin_artifacts', { state: { groupIndex, groups } })}>
                        <FiArrowLeft size={16} />
                        <span>Back to Artifacts</span>
                    </BackArrowButton>
                    <ErrorMessage>No session data available. Please go back to Artifacts page.</ErrorMessage>
                </PageWrapper>
            </>
        );
    }

    const calculateAverages = () => {
        if (assessments.length === 0) {
            return { importance: 0, quality: 0, volume: 0 };
        }

        const totals = assessments.reduce(
            (acc, assessment) => {
                const ratings = assessment.memberRatings || {};
                return {
                    importance: acc.importance + (ratings.importance?.score || 0),
                    quality: acc.quality + (ratings.quality?.score || 0),
                    volume: acc.volume + (ratings.volume?.score || 0),
                };
            },
            { importance: 0, quality: 0, volume: 0 });

        const count = assessments.length;
        return {
            importance: totals.importance / count,
            quality: totals.quality / count,
            volume: totals.volume / count,
        };
    };

    const averages = calculateAverages();
    const contributors = session?.contributors || [];
    const contributorUsers = groupMembers.filter(member => {
        return contributors.some(contributor => {
            const contributorEmail = typeof contributor === 'string' ? contributor : contributor?.email;
            return contributorEmail === member.email;
        });
    });

    const activityName = session?.title || session?.googleDocs?.[0]?.meta?.title ||
        session?.prMeta?.title || session?.figmaFiles?.[0]?.meta?.fileName ||
        'Untitled Activity';

    const displayDate = session?.date || 'N/A';
    const defaultColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

    const getUserColor = (assessment, index) => {
        const submitterEmail = assessment.raterEmail;
        const member = groupMembers.find(m => m.email === submitterEmail);
        return member?.color || defaultColors[index % defaultColors.length];
    };

    const getUserName = (assessment) => {
        const submitterEmail = assessment.raterEmail;
        const member = groupMembers.find(m => m.email === submitterEmail);
        return member?.name || member?.email || 'Unknown';
    };

    const getUserInitial = (assessment) => {
        const submitterEmail = assessment.raterEmail;
        const member = groupMembers.find(m => m.email === submitterEmail);
        return maskInitial(member?.name) || member?.initial || (member?.name || member?.email || 'U').charAt(0).toUpperCase();
    };

    const renderStars = (rating, id) => {

        const starPath = "M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z";
        return (
            <StarRow>
                {[0, 1, 2, 3].map(i => {
                    const fillPct = Math.min(Math.max((rating - i) * 100, 0), 100);
                    const gradId = `sg-${id}-${i}`;
                    return (
                        <svg key={i} width="16" height="16" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
                                    <stop offset={`${fillPct}%`} stopColor="#ffffff" stopOpacity="1" />
                                    <stop offset={`${fillPct}%`} stopColor="#ffffff" stopOpacity="0.25" />
                                </linearGradient>
                            </defs>
                            <path fill={`url(#${gradId})`} d={starPath} />
                        </svg>
                    );
                })}
            </StarRow>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US',
            { year: 'numeric', month: 'short', day: 'numeric' }
        );
    };

    return (
        <>
            <MyHeader groupIndex={groupIndex} groups={groups} />
            <PageWrapper>
                <ContentCard>
                    <BackArrowButton onClick={() => navigate('/admin_artifacts', { state: { groupIndex, groups } })}>
                        <FiArrowLeft size={16} />
                        <span>Back to Artifacts</span>
                    </BackArrowButton>
                    <Title>Work Assessment Results</Title>

                    <SessionInfoCard>
                        <InfoRow>
                            <InfoLabel>Activity:</InfoLabel>
                            <InfoValue>{activityName}</InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel>Submitted by:</InfoLabel>
                            <AvatarGroup>
                                {contributorUsers.length > 0 ? (
                                    contributorUsers.map((member, index) => (
                                        <Avatar key={index} $color={member.color || '#9ca3af'}
                                            title={maskName(member.name || member.email)}
                                        >
                                            {maskName(member.name || member.email).charAt(0).toUpperCase()}
                                        </Avatar>
                                    ))
                                ) : (
                                    <NoContributors>No contributors</NoContributors>
                                )}
                            </AvatarGroup>
                        </InfoRow>

                        <InfoRow>
                            <InfoLabel>Date:</InfoLabel>
                            <InfoValue>{displayDate}</InfoValue>
                        </InfoRow>
                    </SessionInfoCard>

                    <ArtifactsPreview session={session} />

                    <SectionTitle>Overall Rating Summary</SectionTitle>

                    {isLoading ? (
                        <LoadingText>Loading assessment results...</LoadingText>
                    ) : assessments.length === 0 ? (
                        <NoDataText>No assessments submitted yet.</NoDataText>
                    ) : (
                        <>
                            <RatingBarsContainer>
                                <RatingRow>
                                    <RatingLabel>Nature</RatingLabel>
                                    <RatingBarWrapper>
                                        <RatingBar $color="#3b82f6" $width={(averages.importance / 4) * 100}>
                                            {renderStars(averages.importance, 'imp')}
                                        </RatingBar>
                                    </RatingBarWrapper>
                                    <RatingScore $color="#3b82f6">{averages.importance.toFixed(1)}/4.0</RatingScore>
                                </RatingRow>

                                <RatingRow>
                                    <RatingLabel>Quality</RatingLabel>
                                    <RatingBarWrapper>
                                        <RatingBar $color="#10b981" $width={(averages.quality / 4) * 100}>
                                            {renderStars(averages.quality, 'qua')}
                                        </RatingBar>
                                    </RatingBarWrapper>
                                    <RatingScore $color="#10b981">{averages.quality.toFixed(1)}/4.0</RatingScore>
                                </RatingRow>

                                <RatingRow>
                                    <RatingLabel>Volume</RatingLabel>
                                    <RatingBarWrapper>
                                        <RatingBar $color="#f59e0b" $width={(averages.volume / 4) * 100}>
                                            {renderStars(averages.volume, 'vol')}
                                        </RatingBar>
                                    </RatingBarWrapper>
                                    <RatingScore $color="#f59e0b">{averages.volume.toFixed(1)}/4.0</RatingScore>
                                </RatingRow>
                            </RatingBarsContainer>

                            <FeedbackSection>
                                <SectionTitle>Team Feedback</SectionTitle>
                                <FeedbackSubtitle>
                                    Comments from team members ({assessments.length} review{assessments.length !== 1 ? 's' : ''})
                                </FeedbackSubtitle>

                                <FeedbackList>
                                    {assessments
                                        .filter(a => a.memberRatings?.comment)
                                        .map((assessment, index) => (
                                            <FeedbackCard key={assessment.id || index}>
                                                <FeedbackAvatar $color={getUserColor(assessment, index)}>
                                                    {getUserInitial(assessment)}
                                                </FeedbackAvatar>
                                                <FeedbackContent>
                                                    <FeedbackAuthor $color={getUserColor(assessment, index)}>
                                                        {getUserName(assessment)}
                                                    </FeedbackAuthor>
                                                    <FeedbackComment>"{assessment.memberRatings.comment}"</FeedbackComment>
                                                    <FeedbackDate>
                                                        Reviewed on {formatDate(assessment.submittedAt || assessment.createdAt)}
                                                    </FeedbackDate>
                                                </FeedbackContent>
                                            </FeedbackCard>
                                        ))}
                                    {assessments.filter(a => a.memberRatings?.comment).length === 0 && (
                                        <NoCommentsText>No comments have been submitted yet.</NoCommentsText>
                                    )}
                                </FeedbackList>
                            </FeedbackSection>
                        </>
                    )}

                    <RatingModificationHistory eventId={session?.eventId} />

                </ContentCard>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f9fafb;
    min-height: 100vh;
    padding: 30px 20px 60px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 32px;
    max-width: 900px;
    width: 100%;
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 24px;
    text-align: center;
`;

const SessionInfoCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 28px;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const InfoLabel = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    min-width: 140px;
    flex-shrink: 0;
`;

const InfoValue = styled.div`
    font-size: 15px;
    font-weight: 400;
    color: #111827;
    flex: 1;
    text-align: left;
`;

const AvatarGroup = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`;

const Avatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    position: relative;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &::after {
        content: attr(title);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: white;
        color: #111827;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 11px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 100;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.2s ease, visibility 0.2s ease;
    }

    &[title]:hover::after {
        opacity: 1;
        visibility: visible;
    }
`;

const NoContributors = styled.span`
    font-size: 14px;
    color: #6b7280;
`;

const SectionTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 16px;
`;

const LoadingText = styled.div`
    text-align: center;
    color: #6b7280;
    padding: 40px 0;
    font-size: 14px;
`;

const NoDataText = styled.div`
    text-align: center;
    color: #6b7280;
    padding: 40px 0;
    font-size: 14px;
    background-color: #f9fafb;
    border-radius: 8px;
    margin-bottom: 24px;
`;

const RatingBarsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
`;

const RatingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const RatingLabel = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    min-width: 90px;
`;

const RatingBarWrapper = styled.div`
    flex: 1;
    height: 36px;
    background-color: #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
`;

const RatingBar = styled.div`
    height: 100%;
    width: ${props => props.$width}%;
    background-color: ${props => props.$color};
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding-left: 12px;
    transition: width 0.3s ease;
    min-width: ${props => props.$width > 0 ? '100px' : '0'};
`;

const StarRow = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const RatingScore = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.$color};
    min-width: 60px;
    text-align: right;
`;

const FeedbackSection = styled.div`
    margin-bottom: 24px;
`;

const FeedbackSubtitle = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 16px;
`;

const FeedbackList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FeedbackCard = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 12px;
    padding: 16px;
    background-color: #ffffff;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
`;

const FeedbackAvatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FeedbackContent = styled.div`
    flex: 1;
    text-align: left;
`;

const FeedbackAuthor = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.$color};
    margin-bottom: 8px;
    text-align: left;
`;

const FeedbackComment = styled.p`
    font-size: 14px;
    color: #374151;
    line-height: 1.5;
    margin-bottom: 8px;
    text-align: left;
`;

const FeedbackDate = styled.p`
    font-size: 12px;
    color: #9ca3af;
    text-align: left;
`;

const NoCommentsText = styled.div`
    text-align: center;
    color: #6b7280;
    padding: 20px;
    font-size: 13px;
    background-color: #f9fafb;
    border-radius: 8px;
`;

const BackArrowButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #fafafc;
    color: #333333;
    border: 3px solid rgba(0, 0, 0, 0.04);
    border-radius: 11px;
    padding: 8px 14px;
    font-family: "SF Pro Text", system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.29;
    letter-spacing: -0.224px;
    cursor: pointer;
    transition: transform 0.1s ease-in-out;
    margin-bottom: 16px;

    &:active {
        transform: scale(0.95);
    }
`;

const ErrorMessage = styled.div`
    color: #dc2626;
    font-size: 16px;
    margin-bottom: 20px;
    text-align: center;
`;

export default WorkAssessmentResults;
